import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString, TextareaField } from "common";
import { FormikProps } from "formik";
import { isEqual } from "lodash";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { CollectionNode, Merchandise, Permission, ProductObject } from "types";
import { object } from "yup";
import { ProductAttachmentsUpload } from "../ProductAttachmentsUpload";
import { getProductCollectionPayload, ProductCollectionSelector } from "../ProductCollectionSelector";
import {
    getDescriptionInitialValues,
    getMerchandiseDescriptionPayload,
    MerchandiseDescriptionFormValues,
} from "./merchandiseHelper";
import { api } from "@/api/api";
import { Form } from "@/components/Common/Form";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLengthCounter } from "@/components/Field/ContentLengthCounter";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { updateCache } from "@/utils/updateCache";

const validationSchema = object().shape({
    name: requiredString(),
    excerpt: requiredString().max(350, "must have less than 350 characters"),
});

export const MerchandiseDescriptiveContent = ({ merchandise }: { merchandise?: Merchandise }) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const collectionModal = useModal();
    const formikRef = useRef<FormikProps<MerchandiseDescriptionFormValues>>(null);

    const { isGranted } = useAuthorization();
    const showCollectionModification = isGranted(Permission.MODIFY_COLLECTIONS);

    // Fetch collections associated with this product
    const { data: addedCollections, isLoading: isLoadingAddedCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        merchandise?.id ? { product: merchandise.id } : undefined,
        { enabled: !!merchandise?.id && showCollectionModification },
    );

    const addedCollectionPaths = addedCollections.map((node) => node.path ?? "").filter(Boolean);

    const { mutateAsync: saveCollections } = useMutation(
        async ({ newPaths, merchandiseId }: { newPaths: string[]; merchandiseId: string }) => {
            const collectionUpdateOperations = getProductCollectionPayload({
                newPaths,
                oldPaths: addedCollectionPaths,
                addedCollections,
                productId: merchandiseId,
            });

            if (collectionUpdateOperations.length > 0) {
                await api.patch("/collectionNodes", { ops: collectionUpdateOperations });
                invalidate("collectionNodes");
            }
        },
    );

    const { mutateAsync: saveMerchandise } = useMutation(async (payload: MerchandiseDescriptionFormValues) => {
        const merchandisePayload = getMerchandiseDescriptionPayload(payload);
        return merchandise?.id
            ? api.put<Merchandise>(`${MERCHANDISE_API_PATH}/${merchandise.id}`, merchandisePayload)
            : api.post<Merchandise>(MERCHANDISE_API_PATH, merchandisePayload);
    });

    const onSave = async (values: MerchandiseDescriptionFormValues) => {
        const response = await saveMerchandise(values);
        const data = response.data;
        const merchandiseId = data.id;

        // Save collection changes only if any collection path is added or removed
        const hasCollectionChanges =
            showCollectionModification &&
            values.collections &&
            !isEqual([...values.collections].sort(), [...addedCollectionPaths].sort());

        if (hasCollectionChanges) {
            await saveCollections({ newPaths: values.collections!, merchandiseId });
        }

        flash.success("Merchandise saved successfully");

        if (merchandise?.id) {
            updateCache(MERCHANDISE_CACHE_KEY, data);
        } else {
            invalidate(MERCHANDISE_CACHE_KEY);
        }

        if (!merchandise?.id) {
            invalidate(MERCHANDISE_CACHE_KEY);
            history.push(`/products/merchandise/${data.id}/photos`);
        } else {
            invalidate(MERCHANDISE_CACHE_KEY, merchandiseId);
        }
    };

    return (
        <XolaFormik
            enableReinitialize
            initialValues={getDescriptionInitialValues(merchandise, addedCollectionPaths)}
            validationSchema={validationSchema}
            innerRef={formikRef}
            onSubmit={onSave}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                <Form>
                    <ScrollToFieldError />
                    <ContentLayout>
                        <div className="space-y-6">
                            <div className="space-y-4">
                                {showCollectionModification && (
                                    <FormField name="collections" label="">
                                        <ProductCollectionSelector
                                            addedCollectionPaths={addedCollectionPaths}
                                            isLoading={isLoadingAddedCollections}
                                            value={values.collections ?? []}
                                            isOpen={collectionModal.isVisible}
                                            onChange={(collections: string[]) => {
                                                setFieldValue("collections", collections);
                                            }}
                                            onOpenChange={(isOpen) =>
                                                isOpen ? collectionModal.show() : collectionModal.hide()
                                            }
                                        />
                                    </FormField>
                                )}

                                <FormField name="name" label="Title">
                                    <InputField trim isRequired name="name" />
                                </FormField>

                                <FormField
                                    name="excerpt"
                                    label="Excerpt"
                                    tooltip="This text is shown to your customers during the online checkout process. It does not appear in the confirmation email."
                                >
                                    <TextareaField isRequired name="excerpt" rows={6} />
                                    <ContentLengthCounter value={values.excerpt ?? ""} />
                                </FormField>

                                {merchandise && (
                                    <FormField
                                        name="attachments"
                                        label="Attachments"
                                        tooltip="Upload file attachments to be sent in your confirmation email, such as a size chart or certificate of authenticity."
                                    >
                                        <ProductAttachmentsUpload
                                            attachments={merchandise.attachments}
                                            productId={merchandise.id}
                                            productType={ProductObject.WARE}
                                            handleAttachmentChange={(attachments) =>
                                                setFieldValue("attachments", attachments)
                                            }
                                        />
                                    </FormField>
                                )}
                            </div>
                        </div>
                        <ContentLayout.Footer preferenceSidebar>
                            <div className="flex items-center space-x-2">
                                <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                    {merchandise ? "Save" : "Next"}
                                </SubmitButton>

                                <SecondaryButton type="reset" onClick={() => history.push("/products/merchandise")}>
                                    Cancel
                                </SecondaryButton>
                            </div>
                        </ContentLayout.Footer>
                    </ContentLayout>
                </Form>
            )}
        </XolaFormik>
    );
};
